/* Native vendor links work without JavaScript; this adds filtering and optional motion. */
if (!customElements.get('ribra-brands')) {
  customElements.define('ribra-brands', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.events = new AbortController();
      this.list = this.querySelector('[data-list]');
      this.items = Array.from(this.list.children);
      this.arabic = this.dataset.language === 'ar';
      this.querySelectorAll('.ribra-brands__image').forEach((image) => {
        const showFallback = () => {
          image.hidden = true;
          const fallback = image.parentElement.querySelector('[data-logo-fallback]');
          if (fallback) fallback.hidden = false;
        };
        this.listen(image, 'error', showFallback);
        if (image.complete && image.naturalWidth === 0) showFallback();
      });
      if (this.dataset.mode === 'directory') this.setupDirectory();
      else this.setupStrip();
    }

    disconnectedCallback() {
      this.events?.abort();
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      if (this.motionPreference && this.preferenceChanged) {
        this.motionPreference.removeEventListener('change', this.preferenceChanged);
      }
      cancelAnimationFrame(this.frame);
      this.frame = null;
      this.lastTime = null;
      this.initialized = false;
    }

    listen(target, event, callback, options = {}) {
      target.addEventListener(event, callback, { ...options, signal: this.events.signal });
    }

    normalize(value) {
      return value.normalize('NFKD').replace(/[\u0300-\u036f\u064b-\u065f\u0670]/g, '')
        .replace(/[أإآ]/g, 'ا').replace(/ى/g, 'ي').toLocaleLowerCase().trim();
    }

    searchText(value) {
      return this.normalize(value).replace(/[^\p{L}\p{N}]/gu, '');
    }

    setupDirectory() {
      const search = this.querySelector('[data-search]');
      const alphabet = this.querySelector('[data-alphabet]');
      const status = this.querySelector('[data-status]');
      const empty = this.querySelector('[data-empty]');
      const collator = new Intl.Collator('en', { sensitivity: 'base', numeric: true });
      this.items.sort((a, b) => collator.compare(a.dataset.brandName, b.dataset.brandName));
      this.items.forEach((item) => {
        const initial = this.normalize(item.dataset.brandName).charAt(0).toUpperCase();
        item.brandInitial = /^[A-Z]$/.test(initial) ? initial : '#';
        item.brandSearch = this.searchText(item.dataset.brandSearch);
        this.list.append(item);
      });
      // Rebuilding allows Shopify's editor to reconnect the same section safely.
      alphabet.replaceChildren();
      alphabet.hidden = this.items.length < 8;
      const available = new Set(this.items.map((item) => item.brandInitial));
      let activeLetter = 'all';
      const buttons = [];
      const filter = () => {
        const query = this.searchText(search.value);
        let count = 0;
        this.items.forEach((item) => {
          const matches = (activeLetter === 'all' || item.brandInitial === activeLetter) && item.brandSearch.includes(query);
          item.hidden = !matches;
          if (matches) count += 1;
        });
        buttons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.letter === activeLetter)));
        status.textContent = this.arabic ? `عرض ${count} من ${this.items.length} ماركة` : `${count} of ${this.items.length} brands`;
        empty.hidden = count !== 0 || this.items.length === 0;
      };
      const letters = ['all', ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ', '#'].filter((letter) => letter === 'all' || available.has(letter));
      letters.forEach((letter) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ribra-brands__letter';
        button.dataset.letter = letter;
        button.textContent = letter === 'all' ? (this.arabic ? 'الكل' : 'All') : letter;
        button.setAttribute('aria-controls', this.list.id);
        button.setAttribute('aria-pressed', String(letter === 'all'));
        if (letter === '#') button.setAttribute('aria-label', this.arabic ? 'أرقام وحروف أخرى' : 'Numbers and other letters');
        this.listen(button, 'click', () => { activeLetter = letter; filter(); });
        buttons.push(button);
        alphabet.append(button);
      });
      this.listen(search, 'input', filter);
      this.listen(this.querySelector('[data-reset]'), 'click', () => {
        search.value = '';
        activeLetter = 'all';
        filter();
        search.focus();
      });
      this.querySelector('[data-tools]').hidden = false;
      filter();
    }

    setupStrip() {
      this.viewport = this.querySelector('[data-viewport]');
      this.motionButton = this.querySelector('[data-motion]');
      this.motionLabel = this.querySelector('[data-motion-label]');
      this.scrollHint = this.querySelector('[data-scroll-hint]');
      this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = false;
      this.hovered = false;
      this.focused = false;
      this.pointerHeld = false;
      this.inView = true;
      this.interactionUntil = 0;
      this.rtl = this.dir === 'rtl';
      this.enabled = this.dataset.autoplay === 'true';
      this.measure = () => {
        this.maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
        const first = this.list.firstElementChild;
        const gap = parseFloat(getComputedStyle(this.list).columnGap) || 0;
        this.step = first ? first.getBoundingClientRect().width + gap : 0;
        // A full offscreen item is needed to rotate the single live list seamlessly.
        this.loopable = this.step > 0 && this.maxScroll >= this.step;
        this.dataset.overflow = String(this.maxScroll > 1);
        this.scrollHint.hidden = this.maxScroll < 2;
        this.motionButton.hidden = !this.enabled || !this.loopable || this.motionPreference.matches;
        this.schedule();
      };
      this.preferenceChanged = () => { this.lastTime = null; this.measure(); };
      this.motionPreference.addEventListener('change', this.preferenceChanged);
      this.listen(this.motionButton, 'click', () => {
        this.userPaused = !this.userPaused;
        this.motionButton.dataset.paused = String(this.userPaused);
        this.motionLabel.textContent = this.userPaused ? this.dataset.playLabel : this.dataset.pauseLabel;
        this.lastTime = null;
        this.schedule();
      });
      this.listen(this.viewport, 'pointerenter', (event) => {
        if (event.pointerType !== 'touch') this.hovered = true;
      });
      this.listen(this.viewport, 'pointerleave', () => { this.hovered = false; this.schedule(); });
      this.listen(this.viewport, 'focusin', () => { this.focused = true; });
      this.listen(this.viewport, 'focusout', () => {
        queueMicrotask(() => {
          this.focused = this.viewport.contains(document.activeElement);
          this.schedule();
        });
      });
      this.listen(this.viewport, 'pointerdown', () => { this.pointerHeld = true; this.interactionUntil = performance.now() + 7000; }, { passive: true });
      const release = () => {
        if (!this.pointerHeld) return;
        this.pointerHeld = false;
        this.interactionUntil = performance.now() + 7000;
        this.schedule();
      };
      this.listen(window, 'pointerup', release, { passive: true });
      this.listen(window, 'pointercancel', release, { passive: true });
      this.listen(this.viewport, 'wheel', () => { this.interactionUntil = performance.now() + 7000; this.schedule(); }, { passive: true });
      this.listen(this.viewport, 'keydown', (event) => {
        if (event.target !== this.viewport || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        const amount = event.key === 'ArrowRight' ? this.step : -this.step;
        this.viewport.scrollBy({ left: amount, behavior: this.motionPreference.matches ? 'instant' : 'smooth' });
      });
      this.listen(document, 'visibilitychange', () => { this.lastTime = null; this.schedule(); });
      this.resizeObserver = new ResizeObserver(this.measure);
      this.resizeObserver.observe(this.viewport);
      this.resizeObserver.observe(this.list);
      this.intersectionObserver = new IntersectionObserver((entries) => {
        this.inView = entries[0].isIntersecting;
        this.lastTime = null;
        this.schedule();
      });
      this.intersectionObserver.observe(this);
      this.measure();
    }

    canMove() {
      return this.isConnected && this.enabled && this.loopable && !this.motionPreference.matches
        && !this.userPaused && !this.hovered && !this.focused && !this.pointerHeld && this.inView && !document.hidden;
    }

    schedule() {
      if (this.frame || !this.canMove()) return;
      this.lastTime = null;
      this.frame = requestAnimationFrame((time) => this.tick(time));
    }

    tick(time) {
      this.frame = null;
      if (!this.canMove()) { this.lastTime = null; return; }
      const elapsed = this.lastTime === null ? 0 : Math.min(time - this.lastTime, 64);
      // Accumulate fractional pixels so slow movement also works on 1x displays.
      if (this.lastTime === null || time < this.interactionUntil) {
        this.position = this.rtl ? -this.viewport.scrollLeft : this.viewport.scrollLeft;
      }
      this.lastTime = time;
      if (time >= this.interactionUntil) {
        let next = Math.max(0, this.position) + elapsed * 0.022;
        // Rotate only fully offscreen originals. No clones or duplicate tab stops.
        // The same content remains under each visible pixel when the offset resets.
        while (next >= this.step) {
          this.list.append(this.list.firstElementChild);
          next -= this.step;
        }
        this.position = next;
        // Modern browsers expose negative scrollLeft for an RTL scroller.
        this.viewport.scrollLeft = this.rtl ? -next : next;
      }
      this.frame = requestAnimationFrame((nextTime) => this.tick(nextTime));
    }
  });
}
